// @ts-nocheck -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved.
import { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import MuiStepper from "@mui/material/Stepper";
import MuiStep from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Typography from "@mui/material/Typography";
import { StepperContext } from "@/context";
import { useDynamicTranslation } from "@/hooks/useDynamicTranslation";
import { Step } from "./step";

/**
 * @param {{
 * hideStepLabels: Boolean
 * }}
 */
export function Stepper({ children, hideStepLabels = false }: any) {
  const [steps] = useState(
    children?.map((child: any) => ({
      key: child.key,
      label: child.props.label,
      optional: child.props.optional,
    })) ?? []
  );
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set());

  const { t } = useDynamicTranslation();

  const hasNext = () => activeStep < steps.length - 1;
  const isStepOptional = (step: any) => steps[step].optional;
  const isStepSkipped = (step: any) => skipped.has(step);

  const moveNext = () => {
    if (!hasNext()) {
      throw new Error(t("validations:specific.stepper.noNextStep"));
    }

    let newSkipped = skipped;

    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const moveBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error(t("validations:specific.stepper.cantSkipStep"));
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);

      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const stepperContext = useMemo(
    () => ({
      activeStep,
      moveNext,
      moveBack,
      moveStart: handleReset,
      skipStep: handleSkip,
      hasNext,
    }),
    [activeStep]
  );

  return (
    <StepperContext.Provider value={stepperContext}>
      <Box sx={{ width: "100%" }}>
        <MuiStepper activeStep={activeStep} sx={[!hideStepLabels && { my: 2 }]}>
          {!hideStepLabels &&
            steps.map(({ label }: any, index: any) => {
              const stepProps = {};
              const labelProps = {};

              if (isStepOptional(index)) {
                labelProps.optional = (
                  <Typography variant="caption">{t("common:optional")}</Typography>
                );
              }

              if (isStepSkipped(index)) {
                stepProps.completed = false;
              }

              return (
                <MuiStep key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </MuiStep>
              );
            })}
        </MuiStepper>
        {Array.isArray(children) ? children[activeStep] : children}
      </Box>
    </StepperContext.Provider>
  );
}

Stepper.Step = Step;

Stepper.propTypes = {
  children: PropTypes.node,
  hideStepLabels: PropTypes.bool,
};
